

import React, { useEffect, useState, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth, isDeadSessionError, recoverFromDeadSession } from "./useAuth";
import { MenuBar } from './MenuBar';
import hooks from '../actions/hooks';
import store from '../slices/store';

import { setConfig, setDataArrays, setLocationArrays, setTwins } from '../actions';
import { timeframes } from '../screens/Data/config';
import { fetchMessages } from '../slices/messages/actions';
import { mergeBothSettings } from '../slices/settings/actions';
import { Toasts } from './notify';
import { ConfirmHost } from './Modal/confirm';

import LoggedOut from '../screens/LoggedOut';

const ScreenContainer = React.lazy(() => import('./ScreenContainer'));

const Loading = () => <div className="splash">Loading...</div>;
const Redirecting = () => <div className="splash">Redirecting...</div>;


// [BOOT] timing, and a boot that only waits for what the screen needs.
//
// Every boot request goes out at once, but the page draws as soon as the
// screen being shown has its data (SCREEN_NEEDS below); config is always
// first. Settings, for example, draws on config alone and no longer waits
// for graph data. Everything else is applied as it lands.
//
// Data is a two-stage fetch: ONE table first (whichever the selected Data tab
// draws), then the rest in the background. setDataArrays therefore runs twice
// per login and is written to tolerate a partial payload.

let bootCount = 0;                  // module scope: survives re-renders

// The table the visible tab draws, and everything else. We fetch the first one
// alone so the graph can paint, then fetch the rest in the background so tab
// switches are instant. selectedTimeframe comes from localStorage, so it's
// known before any network call happens.
const ALL_TABLES = Object.values(timeframes).map(t => t.tableKey)
    .filter((v, i, a) => a.indexOf(v) === i);

function splitTables() {
    const tf = (() => { try { return localStorage.getItem('data_selectedTimeframe'); } catch (e) { return null; } })();
    const first = (timeframes[tf] && timeframes[tf].tableKey) || timeframes.mins.tableKey;
    return { first: [first], rest: ALL_TABLES.filter(t => t !== first) };
}

// What each screen needs before it can draw. Config is always waited for:
// it carries the subscription status, which decides which screen is even
// allowed. Everything is still REQUESTED at boot; anything not listed for the
// current screen just fills in after the page is already up.
const SCREEN_NEEDS = {
    settings:    [],
    messages:    [],
    controls:    ['twin'],
    ota:         ['twin'],
    automations: ['twin'],
    devices:     ['twin', 'data'],
    data:        ['data', 'locations'],
};

// The screen the user was last on. screenState reads the same key, so this
// is the screen that will actually be drawn.
function savedScreen() {
    try { return localStorage.getItem('last_screen') || 'settings'; } catch (e) { return 'settings'; }
}

const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const fmt   = (n) => n.toFixed(0).padStart(6) + ' ms';

// Times one promise and logs its duration + rough payload size.
async function timed(label, promise) {
    const t = nowMs();
    const out = await promise;
    let size = '';
    try {
        if (out != null) {
            const bytes = new Blob([JSON.stringify(out)]).size;
            size = '  ' + (bytes / 1024).toFixed(1) + ' kB';
        }
    } catch (e) { /* ignore */ }
    console.log('[BOOT]   ' + label.padEnd(16) + fmt(nowMs() - t) + size);
    return out;
}


const Splash = () => {

    const { user, isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect, error } = useAuth();
    const dispatch = useDispatch();

    const [isBootstrapped, setIsBootstrapped] = useState(false);

    // Watch the user's ID, not the user object. Auth0 builds a brand-new
    // `user` object every time it renews the ID token (same person, same
    // contents, different object). With `user` in the dependency list, a
    // token renewal during boot re-fired this effect and ran the whole
    // bootstrap twice. The ID only changes when a different person logs in,
    // which is the only time a re-boot is wanted.
    const userSub = user?.sub;

    const isLoggedOutPage = window.location.pathname === '/logged_out';

    useEffect(() => {
        // The logged-out page must not trigger login or bootstrap.
        if (isLoggedOutPage) return;

        let holder;
        let isMounted = true;

        const fetchDataAndSetSocket = async () => {
            const tBoot = nowMs();
            bootCount++;

            console.log('\n[BOOT] ══════ bootstrap #' + bootCount + ' ══════');
            console.log('[BOOT]   t since page load: ' + fmt(tBoot));

            if (bootCount > 1) {
                console.warn('[BOOT] !! This is bootstrap #' + bootCount + '. The effect re-fired —');
                console.warn('[BOOT] !! that is a full second round of API calls and transforms.');
            }

            try {
                setIsBootstrapped(false);

                const tChunks = nowMs();
                const socketModulePromise = import('./socketService');
                import('./ScreenContainer');
                console.log('[BOOT]   lazy chunks kicked off at ' + fmt(tChunks));

                const tToken = nowMs();
                const token = await getAccessTokenSilently();
                console.log('[BOOT]   getAccessTokenSilently ' + fmt(nowMs() - tToken) +
                            '   <-- Auth0. If this is >1s it is an iframe/refresh round trip, not your code.');

                // Back from the Stripe billing portal? Note it now, reconcile
                // LATER — after the app is on screen (see below). Strip the
                // query param right away so a re-bootstrap doesn't sync twice.
                const portalReturn = new URLSearchParams(window.location.search).get('portal') === 'return';
                if (portalReturn) window.history.replaceState({}, '', window.location.pathname);

                const { first, rest } = splitTables();
                const tFetch = nowMs();

                // ── 1. Ask for everything at once. Nothing waits yet. ────────
                const configP = timed('/get/config', hooks.get('/get/config', token).catch(err => {
                    console.log('config failed:', err);
                    return null;
                }));
                const dataP = timed('/get/data(1)', hooks.post('/get/data', { tables: first }, token).catch(err => {
                    console.log('data failed:', err);
                    return null;
                }));
                const twinP = timed('/get/twin', hooks.get('/get/twin', token).catch(err => {
                    console.log('twin failed:', err);
                    return null;
                }));
                const locP = timed('/get/locations', hooks.get('/get/locations', token).catch(err => {
                    console.log('locations failed:', err);
                    return null;
                }));
                dispatch(fetchMessages(token));

                // ── 2. Config first. It says whether the user is subscribed,
                //    and so which screen is about to be drawn. ────────────────
                const config = await configP;
                if (!isMounted) return;

                if (config) {
                    const t = nowMs();
                    setConfig(config, dispatch);
                    console.log('[BOOT]   setConfig       ' + fmt(nowMs() - t));
                } else {
                    console.log('no config received');
                }

                const subscribed = config?.settings?.subscriptionStatus === 'subscribed';
                const screen = subscribed ? savedScreen() : 'settings';
                const needs = SCREEN_NEEDS[screen] || [];
                console.log('[BOOT]   screen "' + screen + '" waits for [config' +
                            needs.map(n => ',' + n).join('') + ']; data = [' + first.join(',') + ']');

                // ── 3. Apply each of the others the moment it lands. ─────────
                //    Order that matters: data before locations, because
                //    setLocationArrays reuses the series map and colours that
                //    setDataArrays writes. Each step catches its own errors so
                //    one bad payload can't stop the rest.
                const twinApplied = twinP.then(twins => {
                    if (!isMounted) return;
                    if (!twins) { console.log('no twins received'); return; }
                    const t = nowMs();
                    setTwins(twins, dispatch);
                    console.log('[BOOT]   setTwins        ' + fmt(nowMs() - t) +
                                '   (' + Object.keys(twins).length + ' devices, one dispatch EACH)');
                }).catch(err => console.log('applying twins failed:', err));

                const dataApplied = dataP.then(data => {
                    if (!isMounted) return;
                    if (!data) { console.log('no data received'); return; }
                    const t = nowMs();
                    setDataArrays(data, config?.colors || {}, token, dispatch);
                    console.log('[BOOT]   setDataArrays   ' + fmt(nowMs() - t) +
                                '   <-- transposes every document; see actions/index.js for the breakdown');
                }).catch(err => console.log('applying data failed:', err));

                const locApplied = Promise.all([locP, dataApplied]).then(([locations]) => {
                    if (!isMounted || !locations) return;
                    const t = nowMs();
                    setLocationArrays(locations, token, dispatch);
                    console.log('[BOOT]   setLocations    ' + fmt(nowMs() - t));
                }).catch(err => console.log('applying locations failed:', err));

                // ── 4. Draw as soon as THIS screen has what it needs. ─────────
                const applied = { twin: twinApplied, data: dataApplied, locations: locApplied };
                await Promise.all(needs.map(n => applied[n]));
                if (!isMounted) return;

                setIsBootstrapped(true);
                console.log('[BOOT]   setIsBootstrapped(true) at ' + fmt(nowMs() - tBoot) +
                            ' into bootstrap (' + fmt(nowMs() - tFetch) + ' after the requests went out)');

                // Billing reconcile, in the background. Used to run BEFORE the
                // data calls and block the whole boot on a live Stripe lookup.
                // Now the app paints first with the cached status from
                // /get/config, and this corrects it a moment later if Stripe
                // disagrees. Security doesn't depend on it — requireSubscription
                // checks every gated request server-side.
                //
                // mergeBothSettings, NOT setBothSettings: the latter replaces
                // the whole store. Only the three billing fields are merged
                // (the response also carries canDelete, which isn't a setting).
                if (portalReturn) {
                    const tSync = nowMs();
                    fetch('/stripe/sync-billing', {
                        method: 'POST',
                        headers: { Authorization: 'Bearer ' + token },
                    })
                        .then(res => res.ok ? res.json() : Promise.reject(new Error('HTTP ' + res.status)))
                        .then(snap => {
                            console.log('[BOOT]   stripe sync-billing ' + fmt(nowMs() - tSync) + '   (background)');
                            if (!isMounted) return;
                            dispatch(mergeBothSettings({
                                subscriptionStatus: snap.subscriptionStatus,
                                cancelAtPeriodEnd:  snap.cancelAtPeriodEnd,
                                currentPeriodEnd:   snap.currentPeriodEnd,
                            }));
                        })
                        .catch(e => console.log('billing reconcile failed:', e));
                }

                // The remaining tables, in the background. Asked for only after
                // the first table has been applied: its colours have to be in
                // the store before the rest are coloured, and on the Data screen
                // this keeps the extra query from competing with the one the
                // graph is waiting for.
                dataApplied.then(() => {
                    if (!isMounted) return;
                    const tRest = nowMs();
                    return hooks.post('/get/data', { tables: rest }, token)
                        .then(more => {
                            if (!isMounted || !more) return;
                            console.log('[BOOT]   background tables [' + rest.join(',') + '] landed ' + fmt(nowMs() - tRest));
                            setDataArrays(more, store.getState().colors.allocated, token, dispatch);
                        });
                }).catch(err => console.log('background tables failed:', err));

                // Connect live updates once the device snapshot is in the store,
                // so an older snapshot can't land after, and overwrite, a newer
                // device state the socket just delivered. The twin call is
                // small and quick. We deliberately DON'T wait for the graph
                // data here: commands go over this socket (emitCommand drops
                // them until it exists), so on the Controls screen waiting
                // would leave the buttons dead for seconds. The screen being
                // drawn already has its own data in (step 4); a table it isn't
                // showing can at worst miss a few live points until the next
                // refetch, the same trade the background tables already make.
                await twinApplied;
                if (!isMounted) return;

                const tSock = nowMs();
                const { connectSocket } = await socketModulePromise;
                if (!isMounted) return;
                holder = connectSocket(token, dispatch, getAccessTokenSilently, refetchAll);
                console.log('[BOOT]   socket connect  ' + fmt(nowMs() - tSock));

                console.log('[BOOT] ══════ bootstrap #' + bootCount + ' COMPLETE in ' + fmt(nowMs() - tBoot) + ' ══════\n');

            } catch (error) {
                console.error('[BOOT] xx bootstrap FAILED after ' + fmt(nowMs() - tBoot));
                console.error("error details:", error);

                if (isMounted) {
                    setIsBootstrapped(false);
                }

                if (isDeadSessionError(error)) {
                    recoverFromDeadSession(loginWithRedirect);
                }
            }
        };

        const refetchAll = async () => {
            if (store.getState().editLock) {
                console.log('refetch skipped — edit in progress');
                return;
            }

            const tRe = nowMs();
            console.log('\n[BOOT] ══ REFETCH (socket reconnect) ══');

            try {
                const token = await getAccessTokenSilently();

                const [config, data, twins, locations] = await Promise.all([
                    timed('re /get/config', hooks.get('/get/config', token).catch(err => {
                        console.log('refetch config failed:', err);
                        return null;
                    })),
                    timed('re /get/data', hooks.get('/get/data', token).catch(err => {
                        console.log('refetch data failed:', err);
                        return null;
                    })),
                    timed('re /get/twin', hooks.get('/get/twin', token).catch(err => {
                        console.log('refetch twin failed:', err);
                        return null;
                    })),
                    timed('re /get/locations', hooks.get('/get/locations', token).catch(err => {
                        console.log('refetch locations failed:', err);
                        return null;
                    })),
                ]);

                if (!isMounted) return;

                if (config) setConfig(config, dispatch);
                if (data) setDataArrays(data, config?.colors || {}, token, dispatch);
                if (locations) setLocationArrays(locations, token, dispatch);
                if (twins) setTwins(twins, dispatch);

                dispatch(fetchMessages(token));

                console.log('[BOOT] ══ REFETCH done ' + fmt(nowMs() - tRe) + ' ══\n');

            } catch (error) {
                console.error('refetch error:', error);
            }
        };

        if (isAuthenticated && userSub) {
            fetchDataAndSetSocket();
        }

        if (!isAuthenticated && !isLoading) {
            import('./socketService');
            import('./ScreenContainer');
            loginWithRedirect();
        }

        return () => {
            isMounted = false;

            if (holder) {
                console.log("disconnecting a socket", holder.id);
                holder.disconnect();
            }
        };

    }, [isAuthenticated, userSub, isLoading, getAccessTokenSilently, dispatch, loginWithRedirect, isLoggedOutPage]);

    if (isLoggedOutPage) {
        return <LoggedOut />;
    }

    if (isLoading) {
        return <Loading />;
    }

    if (isAuthenticated && !isBootstrapped) {
        return <Loading />;
    }

    if (isAuthenticated && isBootstrapped) {
        return (
            <div id="container">
                <MenuBar />
                <Suspense fallback={<Loading />}>
                    <ScreenContainer />
                </Suspense>
                <Toasts />
                <ConfirmHost />
            </div>
        );
    }

    return <Redirecting />;
};

export default Splash;